import axios from 'axios';

// In dev, use relative /api (proxy in package.json → http://localhost:3004). Else set REACT_APP_API_URL.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
    // Device API methods
    async getDevices() {
        const response = await axios.get(`${API_BASE_URL}/devices`);
        return response.data;
    }

    async getDevice(id) {
        const response = await axios.get(`${API_BASE_URL}/devices/${id}`);
        return response.data;
    }

    async createDevice(deviceData) {
        const response = await axios.post(`${API_BASE_URL}/devices`, deviceData);
        return response.data;
    }

    async syncDevicesFromCatalog() {
        const response = await axios.post(`${API_BASE_URL}/devices/sync`);
        return response.data;
    }

    async updateDevice(id, deviceData) {
        const response = await axios.put(`${API_BASE_URL}/devices/${id}`, deviceData);
        return response.data;
    }

    async deleteDevice(id) {
        const response = await axios.delete(`${API_BASE_URL}/devices/${id}`);
        return response.data;
    }

    async getDeviceFirmware(id) {
        const response = await axios.get(`${API_BASE_URL}/devices/${id}/firmware`);
        return response.data;
    }

    // Firmware API methods
    async getFirmware(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                params.append(key, filters[key]);
            }
        });
        
        const response = await axios.get(`${API_BASE_URL}/firmware?${params}`);
        return response.data;
    }

    async getFirmwareById(id) {
        const response = await axios.get(`${API_BASE_URL}/firmware/${id}`);
        return response.data;
    }

    async uploadFirmware(formData, onUploadProgress) {
        const response = await axios.post(`${API_BASE_URL}/firmware/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: onUploadProgress
        });
        return response.data;
    }

    async downloadFirmware(id) {
        const response = await axios.get(`${API_BASE_URL}/firmware/${id}/download`, {
            responseType: 'blob'
        });
        
        // Extract filename from headers
        const contentDisposition = response.headers['content-disposition'];
        let filename = `firmware-${id}.bin`;
        
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        return {
            blob: response.data,
            filename: filename,
            headers: response.headers
        };
    }

    async deleteFirmware(id) {
        const response = await axios.delete(`${API_BASE_URL}/firmware/${id}`);
        return response.data;
    }

    // Dashboard API methods
    async getDashboardStats() {
        const response = await axios.get(`${API_BASE_URL}/firmware/stats`);
        return response.data;
    }

    async getRecentActivity(limit = 10) {
        const response = await axios.get(`${API_BASE_URL}/firmware/recent?limit=${limit}`);
        return response.data;
    }

    async verifyFirmware() {
        const response = await axios.get(`${API_BASE_URL}/firmware/verify`);
        return response.data;
    }

    async getPublishPreview(firmwareId, { deviceId = '', stationId = '', env = '' } = {}) {
        const params = new URLSearchParams();
        if (deviceId) params.append('device_id', deviceId);
        if (stationId) params.append('station_id', stationId);
        if (env) params.append('env', env);
        const q = params.toString() ? `?${params}` : '';
        const response = await axios.get(`${API_BASE_URL}/firmware/${firmwareId}/publish-preview${q}`);
        return response.data;
    }

    async publishFirmware(id, body = {}) {
        const response = await axios.post(`${API_BASE_URL}/firmware/${id}/publish`, body);
        return response.data;
    }

    async publishAdvanced(id, body = {}) {
        const response = await axios.post(`${API_BASE_URL}/firmware/${id}/publish-advanced`, body);
        return response.data;
    }

    async getAdvancedPublishPreview(firmwareId, body = {}) {
        const response = await axios.post(
            `${API_BASE_URL}/firmware/${firmwareId}/publish-advanced/preview`,
            body
        );
        return response.data;
    }

    async getOtaJob(jobId) {
        const response = await axios.get(`${API_BASE_URL}/firmware/ota-jobs/${encodeURIComponent(jobId)}`);
        return response.data;
    }

    async getIndiaStates() {
        const response = await axios.get(`${API_BASE_URL}/stations/states`);
        return response.data;
    }

    /**
     * Paginated stations. opts: { state, states[], page, pageSize, search }
     */
    async getStations(opts = {}) {
        if (typeof opts === 'boolean') {
            // legacy: getStations(refresh)
            opts = {};
        }
        const params = new URLSearchParams();
        if (opts.state) params.append('state', opts.state);
        if (Array.isArray(opts.states)) {
            opts.states.forEach((s) => params.append('state', s));
        }
        if (opts.page) params.append('page', String(opts.page));
        if (opts.pageSize) params.append('pageSize', String(opts.pageSize));
        if (opts.search) params.append('search', opts.search);
        const q = params.toString() ? `?${params}` : '';
        const response = await axios.get(`${API_BASE_URL}/stations${q}`);
        return response.data;
    }

    async getStationDevices(stationId) {
        const response = await axios.get(
            `${API_BASE_URL}/stations/${encodeURIComponent(stationId)}/devices`
        );
        return response.data;
    }

    async getPublishHistory(limit = 50) {
        const response = await axios.get(`${API_BASE_URL}/firmware/publish-history?limit=${limit}`);
        return response.data;
    }

    async getFleetStatus(top = 10) {
        const response = await axios.get(`${API_BASE_URL}/fleet/status?top=${top}`);
        return response.data;
    }

    async getFleetStations(opts = {}) {
        const params = new URLSearchParams();
        if (opts.sku) params.append('sku', opts.sku);
        if (opts.page) params.append('page', String(opts.page));
        if (opts.pageSize) params.append('pageSize', String(opts.pageSize));
        if (opts.search) params.append('search', opts.search);
        if (opts.outdatedOnly === false) params.append('outdated_only', '0');
        const q = params.toString() ? `?${params}` : '';
        const response = await axios.get(`${API_BASE_URL}/fleet/stations${q}`);
        return response.data;
    }

    async getFleetStationDetail(stationId, sku = '') {
        const q = sku ? `?sku=${encodeURIComponent(sku)}` : '';
        const response = await axios.get(
            `${API_BASE_URL}/fleet/stations/${encodeURIComponent(stationId)}${q}`
        );
        return response.data;
    }

    async getSettings() {
        const response = await axios.get(`${API_BASE_URL}/settings`);
        return response.data;
    }

    async updateSettings(data) {
        const response = await axios.put(`${API_BASE_URL}/settings`, data);
        return response.data;
    }

    // Utility methods
    formatFileSize(bytes) {
        const units = ['B', 'KB', 'MB', 'GB'];
        let size = bytes;
        let unitIndex = 0;
        
        while (size >= 1024 && unitIndex < units.length - 1) {
            size /= 1024;
            unitIndex++;
        }
        
        return `${size.toFixed(2)} ${units[unitIndex]}`;
    }

    formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }

    downloadBlob(blob, filename) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}

// Create singleton instance
const apiService = new ApiService();
export default apiService;